import { PDFDocument, rgb, StandardFonts } from 'pdf-lib';
import fontkit from '@pdf-lib/fontkit';
import { formatCurrency, formatDate } from './invoiceUtils';

// ============================================================================
// EXACT COORDINATES FROM PERFECT REFERENCE PDF
// Page: 595.32 x 841.92 points
// All Y values are "from top of page"
// ============================================================================

const PAGE_HEIGHT = 841.92;
const PAGE_WIDTH = 595.32;

const fromTop = (y) => PAGE_HEIGHT - y;

// ============================================================================
// PRECISE LAYOUT FROM REFERENCE PDF EXTRACTION
// ============================================================================

const LAYOUT = {
  // Header info block (right-aligned) - from reference PDF
  header: {
    codeX: 478.2,           // "Code: R – L/031/22" starts here
    codeY: 78.0,            // Y from top
    invoiceNoX: 434.2,      // "Invoice No:" starts here  
    invoiceNoY: 90.9,
    dateX: 468.9,           // "Date:" starts here
    dateY: 103.9,
    rightEdge: 536,         // Right alignment edge
  },
  
  // Billed to section
  billedTo: {
    x: 87.9,
    startY: 130.5,
    lineHeight: 11.3,       // ~11.3 points between lines
  },
  
  // Delivery info (right side)
  delivery: {
    x: 466.2,
    y1: 206.4,              // "Delivery by Courier"
    y2: 219.8,              // "Advanced copy by email"
  },
  
  // Matter description bar
  matterBar: {
    x: 87.9,
    y: 243.7,
    width: 445,             // Table width ~15.73cm
    lineHeight: 11,
  },
  
  // Main table
  table: {
    leftX: 87.9,            // Left edge aligned with "Billed to"
    width: 445,             // 15.73cm = ~445 points
    rightX: 532.9,          // Right edge
    
    // Column positions
    romanX: 88,             // I., II., III.
    bulletX: 93,            // Bullets ▪
    categoryX: 103,         // Category text (Instruction Fees, etc)
    numberX: 93,            // 1., 2., etc
    descX: 103,             // Description text
    descWidth: 315,         // Max width for text wrapping
    kesX: 426.6,            // "KES" column (from reference)
    amountX: 528,           // Amount right edge
    
    lineHeight: 11,
  },
  
  // Section Y positions (from top)
  sections: {
    professionalFeesY: 278.2,
    // Others calculated dynamically
  },
  
  // Grand Total bar
  grandTotal: {
    x: 87.9,
    height: 23.24,          // 0.82cm
    width: 445,
  },
  
  // Signature section
  signature: {
    stampLabelX: 93.3,
    stampY: 680.8,
    stampImageX: 130,
    stampImageY: 685,
    stampWidth: 49,         // 1.73cm
    stampHeight: 107,       // 3.78cm
    
    signLabelX: 280,
    signImageX: 320,
    signImageY: 685,
    signWidth: 23.24,       // 0.82cm
    signHeight: 82,         // 2.9cm
    
    nameX: 400,
    nameY: 655,
  },
  
  // QR Code - aligned with website QR at x=21.1
  qrCode: {
    x: 21.1,                // Same X as "SCAN TO VERIFY"
    y: 760,                 // Below SCAN TO VERIFY (which is at 746.5)
    size: 50,
  },
};

// ============================================================================
// COLORS
// ============================================================================

const COLORS = {
  professionalFees: rgb(0, 32/255, 96/255),      // #002060
  outlays: rgb(153/255, 51/255, 0),               // #993300
  taxes: rgb(0, 51/255, 0),                       // #003300
  black: rgb(0, 0, 0),
  white: rgb(1, 1, 1),
  grandTotalBg: rgb(214/255, 0/255, 147/255),    // #D60093
  linkBlue: rgb(0, 32/255, 96/255),              // #002060
  rowLine: rgb(0.85, 0.85, 0.85),
};

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

async function loadTemplate() {
  const response = await fetch('/invoice-template.pdf');
  const templateBytes = await response.arrayBuffer();
  return PDFDocument.load(templateBytes);
}

async function loadFonts(pdfDoc) {
  pdfDoc.registerFontkit(fontkit);
  const fonts = {};
  
  try {
    const barlowResponse = await fetch('/fonts/BarlowCondensed-ExtraLight.ttf');
    fonts.barlow = await pdfDoc.embedFont(await barlowResponse.arrayBuffer());
  } catch (e) {
    fonts.barlow = await pdfDoc.embedFont(StandardFonts.Helvetica);
  }
  
  try {
    const neueHaasBoldResponse = await fetch('/fonts/NHaasGroteskTXPro-65Md.ttf');
    fonts.neueHaasBold = await pdfDoc.embedFont(await neueHaasBoldResponse.arrayBuffer());
  } catch (e) {
    fonts.neueHaasBold = await pdfDoc.embedFont(StandardFonts.HelveticaBold);
  }
  
  return fonts;
}

function wrapText(text, font, fontSize, maxWidth) {
  if (!text) return [];
  const words = text.split(' ');
  const lines = [];
  let currentLine = '';

  for (const word of words) {
    const testLine = currentLine ? `${currentLine} ${word}` : word;
    const testWidth = font.widthOfTextAtSize(testLine, fontSize);
    
    if (testWidth > maxWidth && currentLine) {
      lines.push(currentLine);
      currentLine = word;
    } else {
      currentLine = testLine;
    }
  }
  if (currentLine) lines.push(currentLine);
  return lines;
}

function drawRightAlignedText(page, text, rightX, y, font, fontSize, color) {
  const textWidth = font.widthOfTextAtSize(text, fontSize);
  page.drawText(text, {
    x: rightX - textWidth,
    y: fromTop(y),
    size: fontSize,
    font,
    color
  });
}

function drawRowLine(page, y, startX, endX) {
  page.drawLine({
    start: { x: startX, y: fromTop(y) },
    end: { x: endX, y: fromTop(y) },
    thickness: 0.3,
    color: COLORS.rowLine,
  });
}

// ============================================================================
// MAIN PDF GENERATOR
// ============================================================================

export async function generateInvoicePDF(invoiceData) {
  const pdfDoc = await loadTemplate();
  const pages = pdfDoc.getPages();
  const page = pages[0];
  const fonts = await loadFonts(pdfDoc);

  // ========================================
  // HEADER INFO - Right aligned, same X axis as INVOICE title base
  // ========================================
  
  // Code - right aligned to edge
  drawRightAlignedText(
    page, 
    `Code: R – ${invoiceData.matterCode || ''}`, 
    LAYOUT.header.rightEdge, 
    LAYOUT.header.codeY, 
    fonts.barlow, 9, COLORS.black
  );

  // Invoice No - right aligned
  const invoiceNoText = `Invoice No: ${invoiceData.invoiceNumber || ''}`;
  drawRightAlignedText(
    page, 
    invoiceNoText, 
    LAYOUT.header.rightEdge, 
    LAYOUT.header.invoiceNoY, 
    fonts.barlow, 9, COLORS.black
  );

  // Date - right aligned
  drawRightAlignedText(
    page, 
    `Date: ${formatDate(invoiceData.date)}`, 
    LAYOUT.header.rightEdge, 
    LAYOUT.header.dateY, 
    fonts.barlow, 9, COLORS.black
  );

  // ========================================
  // BILLED TO SECTION
  // ========================================
  
  let y = LAYOUT.billedTo.startY;
  
  page.drawText('Billed to:', {
    x: LAYOUT.billedTo.x,
    y: fromTop(y),
    size: 9,
    font: fonts.barlow,
    color: COLORS.black
  });
  y += LAYOUT.billedTo.lineHeight;

  if (invoiceData.client) {
    const clientFields = [
      invoiceData.client.name,
      invoiceData.client.organization,
      ...(invoiceData.client.address || []),
    ].filter(Boolean);

    for (const field of clientFields) {
      page.drawText(field, {
        x: LAYOUT.billedTo.x,
        y: fromTop(y),
        size: 9,
        font: fonts.barlow,
        color: COLORS.black
      });
      y += LAYOUT.billedTo.lineHeight;
    }

    if (invoiceData.client.phone) {
      page.drawText(`Tel: ${invoiceData.client.phone}`, {
        x: LAYOUT.billedTo.x,
        y: fromTop(y),
        size: 9,
        font: fonts.barlow,
        color: COLORS.black
      });
      y += LAYOUT.billedTo.lineHeight;
    }

    if (invoiceData.client.email) {
      page.drawText('Email: ', {
        x: LAYOUT.billedTo.x,
        y: fromTop(y),
        size: 9,
        font: fonts.barlow,
        color: COLORS.black
      });
      const emailLabelWidth = fonts.barlow.widthOfTextAtSize('Email: ', 9);
      page.drawText(invoiceData.client.email, {
        x: LAYOUT.billedTo.x + emailLabelWidth,
        y: fromTop(y),
        size: 9,
        font: fonts.barlow,
        color: COLORS.linkBlue
      });
    }
  }

  // Delivery info - right side, #002060 color
  page.drawText('Delivery by Courier', {
    x: LAYOUT.delivery.x,
    y: fromTop(LAYOUT.delivery.y1),
    size: 8,
    font: fonts.barlow,
    color: COLORS.linkBlue
  });
  page.drawText('Advanced copy by email', {
    x: LAYOUT.delivery.x - 14,  // Slightly left to match reference
    y: fromTop(LAYOUT.delivery.y2),
    size: 8,
    font: fonts.barlow,
    color: COLORS.linkBlue
  });

  // ========================================
  // MATTER DESCRIPTION - Black text, top/bottom borders only
  // ========================================
  
  const matterY = LAYOUT.matterBar.y;
  const matterText = `${invoiceData.invoiceType || 'Invoice'} Relative to ${invoiceData.matterDescription || ''}`;
  const matterLines = wrapText(matterText, fonts.neueHaasBold, 9, LAYOUT.matterBar.width - 10);
  const matterBarHeight = Math.max(22, matterLines.length * 11 + 8);
  
  // Top border line
  page.drawLine({
    start: { x: LAYOUT.table.leftX, y: fromTop(matterY - 2) },
    end: { x: LAYOUT.table.rightX, y: fromTop(matterY - 2) },
    thickness: 0.8,
    color: COLORS.black
  });
  
  // Matter text - black, left aligned
  for (let i = 0; i < matterLines.length; i++) {
    page.drawText(matterLines[i], {
      x: LAYOUT.matterBar.x,
      y: fromTop(matterY + 2 + (i * 11)),
      size: 9,
      font: fonts.neueHaasBold,
      color: COLORS.black
    });
  }
  
  // Bottom border line
  page.drawLine({
    start: { x: LAYOUT.table.leftX, y: fromTop(matterY + matterBarHeight - 4) },
    end: { x: LAYOUT.table.rightX, y: fromTop(matterY + matterBarHeight - 4) },
    thickness: 0.8,
    color: COLORS.black
  });

  y = matterY + matterBarHeight + 6;

  // ========================================
  // I. PROFESSIONAL FEES - #002060, no underline
  // ========================================
  
  page.drawText('I.', {
    x: LAYOUT.table.romanX,
    y: fromTop(y),
    size: 10,
    font: fonts.barlow,
    color: COLORS.professionalFees
  });
  
  page.drawText('Professional Fees', {
    x: LAYOUT.table.romanX + 12,
    y: fromTop(y),
    size: 10,
    font: fonts.barlow,
    color: COLORS.professionalFees
  });

  y += LAYOUT.table.lineHeight + 2;

  // Professional fee categories
  if (invoiceData.professionalFees && invoiceData.professionalFees.length > 0) {
    for (const category of invoiceData.professionalFees) {
      // Category header - #002060, no underline
      if (category.category) {
        page.drawText('▪', {
          x: LAYOUT.table.bulletX,
          y: fromTop(y) - 1,
          size: 7,
          font: fonts.barlow,
          color: COLORS.professionalFees
        });
        
        page.drawText(category.category, {
          x: LAYOUT.table.categoryX,
          y: fromTop(y),
          size: 9,
          font: fonts.barlow,
          color: COLORS.professionalFees
        });
        
        y += LAYOUT.table.lineHeight;
      }

      // Line items
      if (category.items && category.items.length > 0) {
        for (let i = 0; i < category.items.length; i++) {
          const item = category.items[i];
          
          page.drawText(`${i + 1}.`, {
            x: LAYOUT.table.numberX,
            y: fromTop(y),
            size: 9,
            font: fonts.barlow,
            color: COLORS.black
          });

          const descLines = wrapText(item.description || '', fonts.barlow, 9, LAYOUT.table.descWidth);
          for (let j = 0; j < descLines.length; j++) {
            page.drawText(descLines[j], {
              x: LAYOUT.table.descX,
              y: fromTop(y + (j * LAYOUT.table.lineHeight)),
              size: 9,
              font: fonts.barlow,
              color: COLORS.black
            });
          }

          page.drawText('KES', {
            x: LAYOUT.table.kesX,
            y: fromTop(y),
            size: 9,
            font: fonts.barlow,
            color: COLORS.black
          });
          
          drawRightAlignedText(page, formatCurrency(item.amount || 0), LAYOUT.table.amountX, y, fonts.barlow, 9, COLORS.black);

          const itemHeight = Math.max(descLines.length, 1) * LAYOUT.table.lineHeight;
          y += itemHeight;
          
          // Row separator line
          drawRowLine(page, y - 1, LAYOUT.table.leftX, LAYOUT.table.rightX);
        }
      }
    }
  }

  // Subtotal Professional Fees - NO bullet
  y += 3;
  page.drawText('Subtotal Professional Fees', {
    x: LAYOUT.table.bulletX,
    y: fromTop(y),
    size: 9,
    font: fonts.barlow,
    color: COLORS.professionalFees
  });
  
  page.drawText('KES', {
    x: LAYOUT.table.kesX,
    y: fromTop(y),
    size: 9,
    font: fonts.barlow,
    color: COLORS.professionalFees
  });
  
  drawRightAlignedText(page, formatCurrency(invoiceData.subtotalProfessionalFees || 0), LAYOUT.table.amountX, y, fonts.barlow, 9, COLORS.professionalFees);
  
  // Underline for amount
  page.drawLine({
    start: { x: LAYOUT.table.kesX - 5, y: fromTop(y + 2) },
    end: { x: LAYOUT.table.amountX, y: fromTop(y + 2) },
    thickness: 0.5,
    color: COLORS.black
  });

  y += LAYOUT.table.lineHeight + 6;

  // ========================================
  // II. OUTLAYS - #993300, no underline
  // ========================================
  
  page.drawText('II.', {
    x: LAYOUT.table.romanX,
    y: fromTop(y),
    size: 10,
    font: fonts.barlow,
    color: COLORS.outlays
  });
  
  page.drawText('Outlays', {
    x: LAYOUT.table.romanX + 15,
    y: fromTop(y),
    size: 10,
    font: fonts.barlow,
    color: COLORS.outlays
  });

  y += LAYOUT.table.lineHeight + 2;

  // Outlay categories
  if (invoiceData.outlays && invoiceData.outlays.length > 0) {
    for (const category of invoiceData.outlays) {
      if (category.category) {
        page.drawText('▪', {
          x: LAYOUT.table.bulletX,
          y: fromTop(y) - 1,
          size: 7,
          font: fonts.barlow,
          color: COLORS.outlays
        });
        
        page.drawText(category.category, {
          x: LAYOUT.table.categoryX,
          y: fromTop(y),
          size: 9,
          font: fonts.barlow,
          color: COLORS.outlays
        });
        
        y += LAYOUT.table.lineHeight;
      }

      if (category.items && category.items.length > 0) {
        for (let i = 0; i < category.items.length; i++) {
          const item = category.items[i];
          
          page.drawText(`${i + 1}.`, {
            x: LAYOUT.table.numberX,
            y: fromTop(y),
            size: 9,
            font: fonts.barlow,
            color: COLORS.black
          });

          const descLines = wrapText(item.description || '', fonts.barlow, 9, LAYOUT.table.descWidth);
          for (let j = 0; j < descLines.length; j++) {
            page.drawText(descLines[j], {
              x: LAYOUT.table.descX,
              y: fromTop(y + (j * LAYOUT.table.lineHeight)),
              size: 9,
              font: fonts.barlow,
              color: COLORS.black
            });
          }

          page.drawText('KES', {
            x: LAYOUT.table.kesX,
            y: fromTop(y),
            size: 9,
            font: fonts.barlow,
            color: COLORS.black
          });
          
          drawRightAlignedText(page, formatCurrency(item.amount || 0), LAYOUT.table.amountX, y, fonts.barlow, 9, COLORS.black);

          const itemHeight = Math.max(descLines.length, 1) * LAYOUT.table.lineHeight;
          y += itemHeight;
          
          drawRowLine(page, y - 1, LAYOUT.table.leftX, LAYOUT.table.rightX);
        }
      }
    }
  }

  // Subtotal Outlays - NO bullet
  y += 3;
  page.drawText('Subtotal Outlays', {
    x: LAYOUT.table.bulletX,
    y: fromTop(y),
    size: 9,
    font: fonts.barlow,
    color: COLORS.outlays
  });
  
  page.drawText('KES', {
    x: LAYOUT.table.kesX,
    y: fromTop(y),
    size: 9,
    font: fonts.barlow,
    color: COLORS.outlays
  });
  
  drawRightAlignedText(page, formatCurrency(invoiceData.subtotalOutlays || 0), LAYOUT.table.amountX, y, fonts.barlow, 9, COLORS.outlays);
  
  page.drawLine({
    start: { x: LAYOUT.table.kesX - 5, y: fromTop(y + 2) },
    end: { x: LAYOUT.table.amountX, y: fromTop(y + 2) },
    thickness: 0.5,
    color: COLORS.black
  });

  y += LAYOUT.table.lineHeight + 6;

  // ========================================
  // III. TAXES - #003300, no underline
  // ========================================
  
  page.drawText('III.', {
    x: LAYOUT.table.romanX,
    y: fromTop(y),
    size: 10,
    font: fonts.barlow,
    color: COLORS.taxes
  });
  
  page.drawText('Taxes', {
    x: LAYOUT.table.romanX + 18,
    y: fromTop(y),
    size: 10,
    font: fonts.barlow,
    color: COLORS.taxes
  });

  y += LAYOUT.table.lineHeight + 2;

  // VAT line - WITH bullet, #003300
  page.drawText('▪', {
    x: LAYOUT.table.bulletX,
    y: fromTop(y) - 1,
    size: 7,
    font: fonts.barlow,
    color: COLORS.taxes
  });
  
  page.drawText(`Value Added Tax (VAT) at ${invoiceData.vatRate || 16}% of Professional Fees`, {
    x: LAYOUT.table.categoryX,
    y: fromTop(y),
    size: 9,
    font: fonts.barlow,
    color: COLORS.taxes
  });
  
  page.drawText('KES', {
    x: LAYOUT.table.kesX,
    y: fromTop(y),
    size: 9,
    font: fonts.barlow,
    color: COLORS.taxes
  });
  
  drawRightAlignedText(page, formatCurrency(invoiceData.vatAmount || 0), LAYOUT.table.amountX, y, fonts.barlow, 9, COLORS.taxes);

  y += LAYOUT.table.lineHeight + 8;

  // ========================================
  // GRAND TOTAL BAR - #D60093, text centered vertically
  // ========================================
  
  const gtBarHeight = LAYOUT.grandTotal.height;
  
  page.drawRectangle({
    x: LAYOUT.grandTotal.x,
    y: fromTop(y + gtBarHeight),
    width: LAYOUT.grandTotal.width,
    height: gtBarHeight,
    color: COLORS.grandTotalBg,
  });

  // Text centered vertically in bar
  const gtTextY = y + (gtBarHeight / 2) - 3;
  
  page.drawText('Grand Total (amount due)', {
    x: LAYOUT.grandTotal.x + 5,
    y: fromTop(gtTextY),
    size: 11,
    font: fonts.barlow,
    color: COLORS.white
  });
  
  page.drawText('KES', {
    x: LAYOUT.table.kesX,
    y: fromTop(gtTextY),
    size: 11,
    font: fonts.barlow,
    color: COLORS.white
  });
  
  drawRightAlignedText(page, formatCurrency(invoiceData.grandTotal || 0), LAYOUT.table.amountX, gtTextY, fonts.barlow, 11, COLORS.white);

  y += gtBarHeight + 15;

  // ========================================
  // SIGNATURE SECTION
  // ========================================
  
  page.drawText('Stamp:', {
    x: LAYOUT.signature.stampLabelX,
    y: fromTop(y),
    size: 10,
    font: fonts.barlow,
    color: COLORS.black
  });

  // Embed stamp image if provided
  if (invoiceData.stampImage) {
    try {
      const stampData = invoiceData.stampImage.split(',')[1];
      const stampBytes = Uint8Array.from(atob(stampData), c => c.charCodeAt(0));
      const stampImg = invoiceData.stampImage.includes('image/png') 
        ? await pdfDoc.embedPng(stampBytes)
        : await pdfDoc.embedJpg(stampBytes);
      
      page.drawImage(stampImg, {
        x: LAYOUT.signature.stampImageX,
        y: fromTop(y + LAYOUT.signature.stampHeight - 10),
        width: LAYOUT.signature.stampWidth,
        height: LAYOUT.signature.stampHeight,
      });
    } catch (e) {
      console.warn('Could not embed stamp image:', e);
    }
  }

  page.drawText('Sign:', {
    x: LAYOUT.signature.signLabelX,
    y: fromTop(y),
    size: 10,
    font: fonts.barlow,
    color: COLORS.black
  });

  // Embed signature image if provided
  if (invoiceData.signatureImage) {
    try {
      const sigData = invoiceData.signatureImage.split(',')[1];
      const sigBytes = Uint8Array.from(atob(sigData), c => c.charCodeAt(0));
      const sigImg = invoiceData.signatureImage.includes('image/png')
        ? await pdfDoc.embedPng(sigBytes)
        : await pdfDoc.embedJpg(sigBytes);
      
      page.drawImage(sigImg, {
        x: LAYOUT.signature.signImageX,
        y: fromTop(y + LAYOUT.signature.signHeight - 10),
        width: LAYOUT.signature.signWidth,
        height: LAYOUT.signature.signHeight,
      });
    } catch (e) {
      console.warn('Could not embed signature image:', e);
    }
  }

  // Signatory details
  page.drawText('Remington Otieno', {
    x: LAYOUT.signature.nameX,
    y: fromTop(y),
    size: 10,
    font: fonts.barlow,
    color: COLORS.black
  });
  
  page.drawText('Executive Partner', {
    x: LAYOUT.signature.nameX,
    y: fromTop(y + 12),
    size: 9,
    font: fonts.barlow,
    color: COLORS.black
  });
  
  page.drawText('rbo@remingtonllp.com', {
    x: LAYOUT.signature.nameX,
    y: fromTop(y + 24),
    size: 9,
    font: fonts.barlow,
    color: COLORS.linkBlue
  });

  // ========================================
  // QR CODE - Aligned with SCAN TO VERIFY (x=21.1)
  // ========================================
  
  if (invoiceData.verificationQR) {
    try {
      const qrData = invoiceData.verificationQR.split(',')[1];
      const qrBytes = Uint8Array.from(atob(qrData), c => c.charCodeAt(0));
      const qrImage = await pdfDoc.embedPng(qrBytes);
      
      page.drawImage(qrImage, {
        x: LAYOUT.qrCode.x,
        y: fromTop(LAYOUT.qrCode.y + LAYOUT.qrCode.size),
        width: LAYOUT.qrCode.size,
        height: LAYOUT.qrCode.size
      });
    } catch (error) {
      console.warn('Could not embed QR code:', error);
    }
  }

  const pdfBytes = await pdfDoc.save();
  return pdfBytes;
}

// ============================================================================
// EXPORT FUNCTIONS
// ============================================================================

export async function downloadInvoicePDF(invoiceData) {
  const pdfBytes = await generateInvoicePDF(invoiceData);
  const blob = new Blob([pdfBytes], { type: 'application/pdf' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `Invoice_${(invoiceData.invoiceNumber || 'draft').replace(/\//g, '_')}.pdf`;
  link.click();
  URL.revokeObjectURL(url);
}

export async function previewInvoicePDF(invoiceData) {
  const pdfBytes = await generateInvoicePDF(invoiceData);
  const blob = new Blob([pdfBytes], { type: 'application/pdf' });
  return URL.createObjectURL(blob);
}
